import React from "react"
import { NavLink, Outlet, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Dumbbell,
  Activity,
  Utensils,
  Settings,
  LogOut,
  RefreshCw,
  BarChart3,
  Apple,
  Moon,
  Sun,
} from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "./ui/sidebar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb"
import { Button } from "./ui/button"
import { Separator } from "./ui/separator"
import { Switch } from "./ui/switch"
import { Label } from "./ui/label"
import { useDashboardFilters } from "../state/dashboardFilters"
import { useQueryClient } from "@tanstack/react-query"
import { useTheme } from "../hooks/useTheme"

const navItems = [
  {
    title: "Overview",
    url: "/",
    icon: LayoutDashboard,
  },
  {
    title: "Workouts",
    url: "/workouts",
    icon: Dumbbell,
  },
  {
    title: "Exercises",
    url: "/exercises",
    icon: Activity,
  },
  {
    title: "Nutrition",
    url: "/nutrition",
    icon: Utensils,
  },
  {
    title: "App Store",
    url: "/appstore",
    icon: Apple,
  },
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
  },
]

function getPageTitle(pathname) {
  const item = navItems.find((item) => item.url === pathname)
  return item?.title || "Dashboard"
}

export default function AppLayout({ onLogout }) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const { adminOnly, setAdminOnly, autoRefresh, setAutoRefresh, lastRefresh, refresh } =
    useDashboardFilters()
  const { toggleTheme, isDark } = useTheme()

  const handleRefresh = () => {
    queryClient.invalidateQueries()
    refresh()
  }

  const pageTitle = getPageTitle(location.pathname)

  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader>
          <div className="flex items-center gap-2 px-2 py-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <BarChart3 className="h-4 w-4 text-primary-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold">Iridium Analytics</span>
              <span className="text-xs text-muted-foreground">Dashboard</span>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Navigation</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === item.url}
                      tooltip={item.title}
                    >
                      <NavLink to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel>Filters</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="px-2 py-2 space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="admin-only" className="text-sm cursor-pointer">
                    Include admin data
                  </Label>
                  <Switch
                    id="admin-only"
                    checked={adminOnly}
                    onCheckedChange={setAdminOnly}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
                    Auto-refresh
                  </Label>
                  <Switch
                    id="auto-refresh"
                    checked={autoRefresh}
                    onCheckedChange={setAutoRefresh}
                  />
                </div>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={onLogout} tooltip="Logout">
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        {/* Header */}
        <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="h-6" />

          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              {pageTitle !== "Overview" && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{pageTitle}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>

          <div className="ml-auto flex items-center gap-4">
            <span className="text-xs text-muted-foreground hidden sm:inline">
              Last refreshed: {lastRefresh.toLocaleTimeString()}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9"
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="border-t py-4 px-6">
          <div className="flex items-center justify-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} Iridium Fitness. All rights reserved.</p>
          </div>
        </footer>
      </SidebarInset>
    </SidebarProvider>
  )
}
